import React, { useState, useEffect, useMemo } from 'react';
import { Tarefa, StatusTarefa, Usuario } from '../../types';
import { useKanban } from '../../contexts/KanbanContext';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import { useUsuario } from '../../contexts/UsuarioContext';
import api from '../../services/api';
import { RefreshOutlined, Search } from '@mui/icons-material';
import UploadAnexoNovaTarefa from '../tarefas/UploadAnexoNovaTarefa';
import anexoService from '../../services/anexoService';
import usuarioService from '../../services/usuarioService';
import { toast } from 'react-toastify';
import { MIN_DIAS_UTEIS_POR_PRIORIDADE, calcularDataMinimaVencimento, paraInputDate } from '../../utils/regrasPrazo';
import { useBackdropClose } from '../../hooks/useBackdropClose';

interface NovaTarefaModalProps {
  isOpen: boolean;
  onClose: () => void;
  colunaInicial?: string;
  tarefaParaDuplicar?: Tarefa | null;
}

const NovaTarefaModal: React.FC<NovaTarefaModalProps> = ({ isOpen, onClose, colunaInicial = 'Solicitado', tarefaParaDuplicar }) => {
  const { adicionarTarefa } = useKanban();
  const { usuario } = useAuth();
  const { obterUsuariosDepartamento } = useDepartamento();
  const { usuarios } = useUsuario();
  // Removendo dependência direta da função atualizarListaUsuarios
  const carregarUsuarios = async () => {
    try {
      // Recarregar a lista de usuários diretamente do serviço
      const resposta = await usuarioService.obterTodos();
      console.log('Lista de usuários atualizada manualmente');
    } catch (error) {
      console.error('Erro ao atualizar lista de usuários:', error);
    }
  };
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [prioridade, setPrioridade] = useState<'baixa' | 'media' | 'alta' | 'urgente'>('media');
  const [responsavelId, setResponsavelId] = useState('');
  const [dataVencimento, setDataVencimento] = useState('');
  const [erros, setErros] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [atualizandoUsuarios, setAtualizandoUsuarios] = useState(false);
  const [anexos, setAnexos] = useState<File[]>([]);
  const [enviandoAnexos, setEnviandoAnexos] = useState(false);
  const [termoBusca, setTermoBusca] = useState('');
  const [usuariosFiltrados, setUsuariosFiltrados] = useState<Usuario[]>([]);

  // Só admin pode escolher uma Data de Necessidade abaixo do prazo mínimo
  // por prioridade (líder não — ver regra de negócio no backend).
  const podeIgnorarPrazoMinimo = usuario?.tipo === 'admin';
  // Recalcula sempre que a prioridade mudar, para o campo de data já nascer
  // travado no mínimo certo (e não deixar o usuário nem tentar uma data
  // inválida, em vez de só rejeitar depois de enviar).
  const dataMinimaPermitida = useMemo(
    () => (podeIgnorarPrazoMinimo
      ? new Date().toISOString().split('T')[0]
      : paraInputDate(calcularDataMinimaVencimento(prioridade))),
    [podeIgnorarPrazoMinimo, prioridade]
  );

  // Se o usuário já tinha escolhido uma data e ela ficou inválida por causa
  // de uma mudança de prioridade (que aumentou o prazo mínimo exigido),
  // ajusta automaticamente para o novo mínimo em vez de deixar uma data
  // inválida "presa" no campo até o envio falhar.
  useEffect(() => {
    if (dataVencimento && dataVencimento < dataMinimaPermitida) {
      setDataVencimento(dataMinimaPermitida);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMinimaPermitida]);

  // Atualizar lista de usuários quando o modal for aberto
  useEffect(() => {
    if (isOpen) {
      carregarUsuarios();
      
      // Se houver tarefa para duplicar, preencher os campos
      if (tarefaParaDuplicar) {
        setTitulo(tarefaParaDuplicar.titulo + ' (Cópia)');
        setDescricao(tarefaParaDuplicar.descricao || '');
        setPrioridade(tarefaParaDuplicar.prioridade);
        setResponsavelId(tarefaParaDuplicar.responsavel?.id || '');
        setDataVencimento(tarefaParaDuplicar.dataNecessidade || tarefaParaDuplicar.dataVencimento || '');
      } else {
        // Limpar campos se não houver tarefa para duplicar
        setTitulo('');
        setDescricao('');
        setPrioridade('media');
        // Auto-atribuição padrão para admin/comum (expert não)
        if (usuario && usuario.tipo !== 'expert') {
          setResponsavelId(usuario.id);
        } else {
          setResponsavelId('');
        }
        setDataVencimento('');
      }
    }
  }, [isOpen, tarefaParaDuplicar]);
  
  // Filtrar usuários com base no termo de busca
  useEffect(() => {
    if (usuarios.length > 0 && usuario) {
      let baseUsuarios = [...usuarios];

      // Garantir que admin/comum enxerguem a si mesmos (auto-atribuição). Experts continuam sem self-assign.
      if (usuario.tipo !== 'expert') {
        const jaTemUsuarioAtual = baseUsuarios.some(u => u.id === usuario.id);
        console.log('[NovaTarefaModal] Verificando usuário atual na lista:', {
          usuarioAtualId: usuario.id,
          usuarioAtualNome: usuario.nome,
          jaTemNaLista: jaTemUsuarioAtual,
          totalUsuarios: baseUsuarios.length
        });
        
        if (!jaTemUsuarioAtual) {
          console.log('[NovaTarefaModal] Adicionando usuário atual à lista');
          baseUsuarios = [
            {
              id: usuario.id,
              nome: usuario.nome,
              email: usuario.email,
              cargo: usuario.cargo || '',
              departamento_id: (usuario as any).departamento_id || (usuario as any).departamentoId || '',
              departamentoId: (usuario as any).departamentoId || (usuario as any).departamento_id || '',
              departamento_nome: (usuario as any).departamento_nome || '',
              tipo: usuario.tipo,
              avatar: usuario.avatar,
              ehLider: usuario.ehLider || false,
              tarefasAtivas: (usuario as any).tarefasAtivas || 0,
              tarefasConcluidas: (usuario as any).tarefasConcluidas || 0,
              dataCriacao: (usuario as any).dataCriacao || '',
              ativo: true
            },
            ...baseUsuarios
          ];
        }
      }

      let filtrados = baseUsuarios;

      // Experts: não podem auto-atribuir e só veem Tráfego + Daniel Aguiar
      if (usuario.tipo === 'expert') {
        filtrados = filtrados
          .filter(u => u.id !== usuario.id)
          .filter(u => u.departamento_nome === 'Tráfego' || u.nome === 'Daniel Aguiar');
      }
      
      // Aplicar filtro de busca
      filtrados = filtrados.filter(u => {
        if (!termoBusca) return true;
        const termoLower = termoBusca.toLowerCase();
        return (
          u.nome.toLowerCase().includes(termoLower) ||
          (u.cargo && u.cargo.toLowerCase().includes(termoLower)) ||
          (u.departamento_nome && u.departamento_nome.toLowerCase().includes(termoLower))
        );
      })
      .sort((a, b) => {
        // Colocar o usuário atual sempre no topo (se não for expert)
        if (usuario.tipo !== 'expert') {
          if (a.id === usuario.id) return -1;
          if (b.id === usuario.id) return 1;
        }
        return a.nome.localeCompare(b.nome);
      });
      
      console.log('[NovaTarefaModal] Usuários filtrados:', filtrados.length);
      setUsuariosFiltrados(filtrados);
    }
  }, [termoBusca, usuarios, usuario]);
  
  // Função para atualizar manualmente a lista de usuários
  const handleAtualizarUsuarios = async () => {
    setAtualizandoUsuarios(true);
    await carregarUsuarios();
    setAtualizandoUsuarios(false);
  };

  const validarFormulario = () => {
    const novosErros: { [key: string]: string } = {};

    if (!titulo.trim()) {
      novosErros.titulo = 'O título é obrigatório';
    }

    if (!descricao.trim()) {
      novosErros.descricao = 'A descrição da tarefa é obrigatória';
    }

    if (!responsavelId) {
      novosErros.responsavel = 'Selecione um responsável';
    }

    if (!dataVencimento) {
      novosErros.dataVencimento = 'A data de necessidade é obrigatória';
    } else if (dataVencimento < dataMinimaPermitida) {
      novosErros.dataVencimento = podeIgnorarPrazoMinimo
        ? 'A data de necessidade não pode ser anterior a hoje'
        : `Para prioridade "${prioridade}", a data mínima é ${new Date(dataMinimaPermitida + 'T12:00:00').toLocaleDateString('pt-BR')}`;
    }

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  // Função para fazer upload de anexos após criar a tarefa
  const uploadAnexos = async (tarefaId: string) => {
    if (anexos.length === 0) return;
    
    setEnviandoAnexos(true);
    toast.info(`Enviando ${anexos.length} anexo(s)...`, { autoClose: false, toastId: 'uploading-anexos' });
    
    try {
      const sucessos = [];
      const falhas = [];
      
      // Upload de cada anexo
      for (const arquivo of anexos) {
        try {
          const response = await anexoService.uploadAnexo(tarefaId, arquivo);
          if (response.success) {
            sucessos.push(arquivo.name);
          } else {
            falhas.push(arquivo.name);
          }
        } catch (error) {
          console.error(`Erro ao enviar anexo ${arquivo.name}:`, error);
          falhas.push(arquivo.name);
        }
      }
      
      // Fechar o toast de "enviando"
      toast.dismiss('uploading-anexos');
      
      // Mostrar resultado
      if (sucessos.length > 0) {
        toast.success(`${sucessos.length} anexo(s) enviado(s) com sucesso!`);
      }
      
      if (falhas.length > 0) {
        toast.error(`Falha ao enviar ${falhas.length} anexo(s)`);
      }
    } catch (error) {
      console.error('Erro ao enviar anexos:', error);
      toast.error('Erro ao enviar anexos');
    } finally {
      setEnviandoAnexos(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validarFormulario()) return;
    if (!usuario) return;
    
    setIsLoading(true);
    
    try {
      // Encontrar o responsável selecionado para obter seu departamento_id
      const responsavelSelecionado = usuarios.find(u => u.id === responsavelId);
      if (!responsavelSelecionado) {
        setErros({ responsavel: 'Responsável não encontrado' });
        setIsLoading(false);
        return;
      }
      
      console.log('Responsável selecionado:', responsavelSelecionado);
      
      const novaTarefaData = {
        titulo,
        descricao,
        prioridade,
        status: colunaInicial,
        responsavel_id: responsavelId,
        departamento_id: responsavelSelecionado.departamento_id || responsavelSelecionado.departamentoId, // Usar o campo correto dependendo do que estiver disponível
        // Data de Necessidade: quando o CRIADOR precisa da entrega.
        // A Data de Vencimento (compromisso real) fica em branco aqui —
        // o RESPONSÁVEL a define depois, respeitando o prazo mínimo de
        // antecedência em dias úteis da prioridade da tarefa.
        data_necessidade: dataVencimento || null
        // Removida a data_criacao para permitir que o backend defina usando CURRENT_TIMESTAMP
      };
      
      console.log('Dados da nova tarefa:', novaTarefaData);
      
      // Preparar objeto da tarefa para o contexto Kanban
      const responsavel = usuarios.find(u => u.id === responsavelId);
      
      console.log('Criando nova tarefa com responsável:', responsavel?.nome, '(ID:', responsavelId, ')');
      console.log('Usuário atual (criador):', usuario?.nome, '(ID:', usuario?.id, ')');
      
      const novaTarefa: Tarefa = {
        id: '', // Será preenchido pela API
        titulo,
        descricao,
        prioridade,
        status: colunaInicial as StatusTarefa,
        responsavel: responsavel || { 
          id: responsavelId, 
          nome: 'Carregando...', 
          email: '', 
          cargo: '',
          departamentoId: '',
          tipo: 'comum',
          avatar: '/profilepicture.png', // Usando imagem padrão
          ehLider: false,
          tarefasAtivas: 0,
          tarefasConcluidas: 0,
          dataCriacao: ''
        },
        criador: usuario,
        // A dataCriacao será definida pelo backend usando CURRENT_TIMESTAMP
        dataCriacao: '', // Será preenchida pela resposta da API
        // Vencimento fica em branco na criação — o responsável define depois
        dataVencimento: null,
        dataNecessidade: dataVencimento ? dataVencimento : null,
        comentarios: [],
        departamentoId: responsavel?.departamentoId || ''
      };
      
      // Certificar-se de que os IDs estão corretos nos dados da API
      const dadosAPIAtualizados = {
        ...novaTarefaData,
        responsavel_id: responsavelId,
        criador_id: usuario?.id
      };
      
      console.log('Dados da API para nova tarefa:', dadosAPIAtualizados);
      
      // Passar os dados da API junto com a tarefa para o contexto
      await adicionarTarefa(novaTarefa, dadosAPIAtualizados);
      
      // Se tiver anexos, fazer upload
      // A tarefa já foi criada e o ID está disponível em novaTarefa.id após adicionarTarefa
      if (anexos.length > 0 && novaTarefa.id) {
        await uploadAnexos(novaTarefa.id);
      }
      
      resetarFormulario();
      onClose();
    } catch (error: any) {
      console.error('Erro ao criar tarefa:', error);
      // Mantém o modal aberto com os dados preenchidos — antes ele fechava
      // mesmo quando a criação falhava (ex: violação de prazo mínimo),
      // dando a falsa impressão de que a tarefa tinha sido criada.
      setErros({ submit: error?.message || 'Erro ao criar tarefa. Tente novamente.' });
    } finally {
      setIsLoading(false);
    }
  };
  
  const resetarFormulario = () => {
    setTitulo('');
    setDescricao('');
    setPrioridade('media');
    setResponsavelId('');
    setDataVencimento('');
    setAnexos([]);
    setErros({});
  };
  
  // PRECISA vir antes de qualquer "return" condicional — hooks do React têm
  // que rodar sempre na mesma ordem em todo render.
  const backdropClose = useBackdropClose(onClose);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      {...backdropClose}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Nova Tarefa</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Título */}
            <div className="md:col-span-2">
              <label htmlFor="titulo" className="block text-sm font-medium text-gray-700 mb-1">
                Título *
              </label>
              <input
                type="text"
                id="titulo"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                className={`w-full p-2 border ${erros.titulo ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary`}
                placeholder="Digite o título da tarefa"
              />
              {erros.titulo && <p className="mt-1 text-sm text-red-500">{erros.titulo}</p>}
            </div>
            
            {/* Descrição */}
            <div className="md:col-span-2">
              <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-1">
                Descrição *
              </label>
              <textarea
                id="descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={4}
                className={`w-full p-2 border ${erros.descricao ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary`}
                placeholder="Descreva os detalhes da tarefa"
              />
              {erros.descricao && <p className="mt-1 text-sm text-red-500">{erros.descricao}</p>}
            </div>
            
            {/* Prioridade */}
            <div>
              <label htmlFor="prioridade" className="block text-sm font-medium text-gray-700 mb-1">
                Prioridade
              </label>
              <select
                id="prioridade"
                value={prioridade}
                onChange={(e) => setPrioridade(e.target.value as 'baixa' | 'media' | 'alta' | 'urgente')}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary"
              >
                <option value="baixa">Baixa</option>
                <option value="media">Média</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
              <p className="text-xs text-gray-600 mt-1.5 bg-blue-50 border border-blue-100 rounded-md px-2 py-1.5">
                Prazo mínimo de antecedência para <strong>{prioridade.charAt(0).toUpperCase() + prioridade.slice(1)}</strong>:{' '}
                <strong>{MIN_DIAS_UTEIS_POR_PRIORIDADE[prioridade] ?? MIN_DIAS_UTEIS_POR_PRIORIDADE.media} dia(s) útil(eis)</strong>{' '}
                (mais antecedência é sempre permitido).
              </p>
            </div>

            {/* Responsável */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="responsavel" className="block text-sm font-medium text-gray-700">
                  Responsável *
                </label>
                <button 
                  type="button" 
                  onClick={handleAtualizarUsuarios}
                  className="flex items-center text-xs text-blue-600 hover:text-blue-800"
                  disabled={atualizandoUsuarios}
                >
                  <RefreshOutlined className={`h-4 w-4 mr-1 ${atualizandoUsuarios ? 'animate-spin' : ''}`} />
                  {atualizandoUsuarios ? 'Atualizando...' : 'Atualizar lista'}
                </button>
              </div>
              
              {/* Campo de busca para filtrar responsáveis */}
              <div className="relative mb-2">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Buscar por nome, cargo ou departamento"
                  value={termoBusca}
                  onChange={(e) => setTermoBusca(e.target.value)}
                  className="pl-10 w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary"
                />
              </div>
              
              {/* Lista de usuários estilizada */}
              <div className={`border ${erros.responsavel ? 'border-red-500' : 'border-gray-300'} rounded-md overflow-hidden max-h-48 overflow-y-auto`}>
                {usuariosFiltrados.length === 0 ? (
                  <div className="p-3 text-center text-gray-500 bg-gray-50">
                    {termoBusca ? 'Nenhum usuário encontrado com esse termo.' : 'Nenhum usuário disponível.'}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    <div 
                      className={`p-2 hover:bg-gray-100 cursor-pointer ${responsavelId === '' ? 'bg-gray-50' : ''}`}
                      onClick={() => setResponsavelId('')}
                    >
                      <p className="text-gray-500 italic">Selecione um responsável</p>
                    </div>
                    {usuariosFiltrados.map((usuario) => {
                      // Obter o nome do departamento
                      let departamentoNome = usuario.departamento_nome;
                      if (!departamentoNome && usuario.departamentoId) {
                        const departamento = obterUsuariosDepartamento(usuario.departamentoId);
                        if (departamento.length > 0) {
                          departamentoNome = departamento[0].nome;
                        }
                      }
                      
                      const isSelected = responsavelId === usuario.id;
                      
                      return (
                        <div 
                          key={usuario.id} 
                          className={`p-2 hover:bg-gray-100 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                          onClick={() => setResponsavelId(usuario.id)}
                        >
                          <div className="flex items-center">
                            {isSelected && (
                              <div className="mr-2 text-blue-600">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                            <div className={`flex-1 ${isSelected ? 'font-medium text-blue-700' : ''}`}>
                              <p className="text-sm font-medium">{usuario.nome}</p>
                              <p className="text-xs text-gray-500">
                                {usuario.cargo || 'Membro'} - {departamentoNome || 'Sem departamento'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {erros.responsavel && <p className="mt-1 text-sm text-red-500">{erros.responsavel}</p>}
            </div>
            
            {/* Data de necessidade */}
            <div className="mb-4">
              <label htmlFor="dataVencimento" className="block text-sm font-medium text-gray-700 mb-1">
                Data de Necessidade *
              </label>
              <p className="text-xs text-gray-500 mb-1">Quando você precisa que esta tarefa esteja concluída.</p>
              <input
                type="date"
                id="dataVencimento"
                value={dataVencimento}
                onChange={(e) => {
                  const valor = e.target.value;
                  // Reforça o mínimo mesmo se o navegador aceitar digitação
                  // manual de uma data fora do range do atributo `min`.
                  setDataVencimento(valor && valor < dataMinimaPermitida ? dataMinimaPermitida : valor);
                }}
                className={`w-full p-2 border ${erros.dataVencimento ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                min={dataMinimaPermitida}
              />
              {erros.dataVencimento && <p className="mt-1 text-sm text-red-500">{erros.dataVencimento}</p>}
              <p className="text-xs text-gray-500 mt-1">
                O responsável definirá livremente a Data de Vencimento (compromisso de entrega) ao receber a tarefa — podendo antecipar a entrega antes desta Data de Necessidade.
              </p>
            </div>

            {/* Upload de anexos */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Anexos (opcional)
              </label>
              <UploadAnexoNovaTarefa 
                anexos={anexos} 
                setAnexos={setAnexos} 
                enviandoAnexos={enviandoAnexos}
              />
            </div>

            {erros.submit && (
              <p className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {erros.submit}
              </p>
            )}

            {/* Botão de submissão */}
            <div className="flex justify-center mt-6 space-x-4">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                onClick={() => {
                  resetarFormulario();
                  onClose();
                }}
                disabled={isLoading || enviandoAnexos}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading || enviandoAnexos}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Criando...
                  </>
                ) : enviandoAnexos ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enviando anexos...
                  </>
                ) : 'Criar Tarefa'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NovaTarefaModal;
